import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";

export interface ContentInterviewTopic {
  title: string;
  source: "manual" | "ai";
  notes: string;
  category: string;
}

export interface ContentInterview {
  id: number;
  user_id: number;
  organization_id: number;
  status: string;
  topics: ContentInterviewTopic[];
  pricing_plan: string | null;
  amount_cents: number | null;
  stripe_checkout_session_id: string | null;
  calendly_event_url: string | null;
  scheduled_at: string | null;
  created_at: string;
  updated_at: string;
}

class ContentInterviewService {
  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }

  async list(): Promise<ContentInterview[]> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/content_interviews`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async create(): Promise<ContentInterview> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/content_interviews`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async show(id: number): Promise<ContentInterview> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/content_interviews/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async update(id: number, data: Partial<Pick<ContentInterview, "status" | "topics" | "pricing_plan" | "calendly_event_url" | "scheduled_at">>): Promise<ContentInterview> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/content_interviews/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async confirmPayment(id: number, pricingPlan: string, stripeCheckoutSessionId: string): Promise<ContentInterview> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/content_interviews/${id}/confirm_payment`,
      { pricing_plan: pricingPlan, stripe_checkout_session_id: stripeCheckoutSessionId },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async suggestTopics(blueprintId: number): Promise<ContentInterviewTopic[]> {
    // Start async job
    const startResponse = await axios.post(
      `${getApiUrlWithVersion()}/content_interviews/suggest_topics`,
      { blueprint_id: blueprintId },
      { headers: this.getAuthHeaders() }
    );
    const jobId = startResponse.data.job_id;

    // Poll for results
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const statusResponse = await axios.get(
        `${getApiUrlWithVersion()}/content_interviews/suggest_topics_status/${jobId}`,
        { headers: this.getAuthHeaders() }
      );
      const result = statusResponse.data;
      if (result.status === "completed") {
        return result.topics;
      }
      if (result.status === "failed") {
        throw new Error(result.error || "Topic generation failed");
      }
    }
    throw new Error("Topic generation timed out");
  }
}

const contentInterviewService = new ContentInterviewService();
export default contentInterviewService;
